/*
 * =========================================================
 * CHEMARRY CAT CARE v1.0
 * Higiene y cuidado para gatos
 * Compatible con Shopify Dawn 15.4.1
 * =========================================================
 */

(() => {
  'use strict';

  const SELECTORS = {
    root: 'chemarry-cat-care',
    grid: '[data-care-grid]',
    card: '[data-care-card]',
    filter: '[data-care-filter]',
    search: '[data-care-search]',
    count: '[data-care-count]',
    empty: '[data-care-empty]',
    previous: '[data-care-previous]',
    next: '[data-care-next]',
    variant: '[data-care-variant]',
    add: '[data-care-add]',
    buttonText: '[data-care-button-text]',
    availability: '[data-care-availability]',
    currentPrice: '[data-care-price-current]',
    comparePrice: '[data-care-price-compare]',
    productImage: '[data-care-product-image]',
    toast: '[data-care-toast]',
    toastText: '[data-care-toast-text]'
  };

  class ChemarryCatCare extends HTMLElement {
    constructor() {
      super();

      this.grid = null;
      this.cards = [];
      this.filters = [];
      this.search = null;
      this.count = null;
      this.empty = null;
      this.previousButton = null;
      this.nextButton = null;
      this.toast = null;
      this.toastText = null;

      this.activeFilter = 'all';
      this.searchTerm = '';
      this.toastTimer = null;
      this.resizeTimer = null;

      this.boundHandleResize = this.handleResize.bind(this);
      this.boundHandleSectionLoad = this.handleSectionLoad.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === 'true') return;

      this.dataset.initialized = 'true';

      this.cacheElements();
      this.bindEvents();
      this.updateProducts();
      this.updateNavigation();

      window.addEventListener('resize', this.boundHandleResize);

      document.addEventListener(
        'shopify:section:load',
        this.boundHandleSectionLoad
      );
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.boundHandleResize);

      document.removeEventListener(
        'shopify:section:load',
        this.boundHandleSectionLoad
      );

      window.clearTimeout(this.toastTimer);
      window.clearTimeout(this.resizeTimer);
    }

    cacheElements() {
      this.grid = this.querySelector(SELECTORS.grid);

      this.cards = Array.from(
        this.querySelectorAll(SELECTORS.card)
      );

      this.filters = Array.from(
        this.querySelectorAll(SELECTORS.filter)
      );

      this.search = this.querySelector(SELECTORS.search);
      this.count = this.querySelector(SELECTORS.count);
      this.empty = this.querySelector(SELECTORS.empty);

      this.previousButton = this.querySelector(
        SELECTORS.previous
      );

      this.nextButton = this.querySelector(
        SELECTORS.next
      );

      this.toast = this.querySelector(SELECTORS.toast);
      this.toastText = this.querySelector(SELECTORS.toastText);
    }

    bindEvents() {
      this.filters.forEach((filter) => {
        filter.addEventListener('click', () => {
          this.handleFilter(filter);
        });
      });

      if (this.search) {
        this.search.addEventListener('input', (event) => {
          this.searchTerm = this.normalizeText(
            event.target.value
          );

          this.updateProducts();
        });

        this.search.addEventListener('search', (event) => {
          this.searchTerm = this.normalizeText(
            event.target.value
          );

          this.updateProducts();
        });
      }

      if (this.previousButton) {
        this.previousButton.addEventListener('click', () => {
          this.scrollGrid(-1);
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener('click', () => {
          this.scrollGrid(1);
        });
      }

      if (this.grid) {
        this.grid.addEventListener(
          'scroll',
          () => this.updateNavigation(),
          { passive: true }
        );
      }

      this.querySelectorAll(SELECTORS.variant).forEach(
        (selector) => {
          selector.addEventListener('change', (event) => {
            this.handleVariantChange(event.currentTarget);
          });
        }
      );

      this.querySelectorAll(SELECTORS.add).forEach(
        (button) => {
          button.addEventListener('click', (event) => {
            this.handleAddToCart(event.currentTarget);
          });
        }
      );

      if (Shopify && Shopify.designMode) {
        this.bindThemeEditorEvents();
      }
    }

    bindThemeEditorEvents() {
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          const card = event.target.closest(
            SELECTORS.card
          );

          if (!card) return;

          card.hidden = false;

          window.requestAnimationFrame(() => {
            card.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center'
            });
          });
        }
      );
    }

    handleFilter(filter) {
      const requestedFilter =
        filter.dataset.careFilter || 'all';

      this.activeFilter = this.normalizeCategory(
        requestedFilter
      );

      this.filters.forEach((item) => {
        const isActive = item === filter;

        item.classList.toggle('is-active', isActive);

        item.setAttribute(
          'aria-pressed',
          isActive ? 'true' : 'false'
        );
      });

      this.updateProducts();
    }

    updateProducts() {
      let visibleProducts = 0;
      let configuredProducts = 0;

      this.cards.forEach((card) => {
        const configured =
          card.dataset.careConfigured !== 'false';

        if (configured) configuredProducts += 1;

        const categories = this.getCardCategories(card);
        const searchableContent = this.normalizeText(
          card.dataset.careSearch || card.textContent || ''
        );

        const matchesCategory =
          this.activeFilter === 'all' ||
          categories.includes(this.activeFilter);

        const matchesSearch =
          this.searchTerm === '' ||
          searchableContent.includes(this.searchTerm);

        const shouldShow =
          configured &&
          matchesCategory &&
          matchesSearch;

        card.hidden = !shouldShow;

        card.setAttribute(
          'aria-hidden',
          shouldShow ? 'false' : 'true'
        );

        if (shouldShow) visibleProducts += 1;
      });

      this.updateCount(
        visibleProducts,
        configuredProducts
      );

      if (this.empty) {
        this.empty.hidden = visibleProducts !== 0;
      }

      if (this.grid) {
        this.grid.scrollTo({
          left: 0,
          behavior: this.prefersReducedMotion()
            ? 'auto'
            : 'smooth'
        });
      }

      window.requestAnimationFrame(() => {
        this.updateNavigation();
      });
    }

    updateCount(visible, total) {
      if (!this.count) return;

      const singular =
        this.dataset.productSingular || 'producto';

      const plural =
        this.dataset.productPlural || 'productos';

      if (visible === total) {
        this.count.textContent =
          `${total} ${total === 1 ? singular : plural}`;

        return;
      }

      this.count.textContent =
        `Mostrando ${visible} de ${total} ${plural}`;
    }

    getCardCategories(card) {
      const rawCategories =
        card.dataset.careCategories || '';

      return rawCategories
        .split(/[|,]/)
        .map((category) =>
          this.normalizeCategory(category)
        )
        .filter(Boolean);
    }

    normalizeCategory(value) {
      const normalized = this.normalizeText(value);

      const aliases = {
        todo: 'all',
        todos: 'all',
        all: 'all',

        arena: 'arena',
        arenas: 'arena',

        limpieza: 'cleaning',
        cleaning: 'cleaning',
        shampoo: 'cleaning',
        champu: 'cleaning',

        dental: 'dental',
        dientes: 'dental',

        pelaje: 'coat',
        coat: 'coat',
        cepillo: 'coat',
        cepillos: 'coat',

        accesorio: 'accessories',
        accesorios: 'accessories',
        accessories: 'accessories',

        higiene: 'hygiene',
        hygiene: 'hygiene',

        otro: 'other',
        otros: 'other',
        other: 'other'
      };

      return aliases[normalized] || normalized;
    }

    normalizeText(value) {
      return String(value || '')
        .toLocaleLowerCase('es')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\p{L}\p{N}\s]/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }

    scrollGrid(direction) {
      if (!this.grid) return;

      const visibleCards = this.cards.filter(
        (card) => !card.hidden
      );

      const firstCard = visibleCards[0];

      if (!firstCard) return;

      const gridStyles = window.getComputedStyle(
        this.grid
      );

      const gap =
        parseFloat(gridStyles.columnGap) ||
        parseFloat(gridStyles.gap) ||
        12;

      const cardWidth =
        firstCard.getBoundingClientRect().width;

      const visibleColumns =
        window.innerWidth < 750 ? 1 : 2;

      const distance =
        (cardWidth + gap) * visibleColumns;

      this.grid.scrollBy({
        left: direction * distance,
        behavior: this.prefersReducedMotion()
          ? 'auto'
          : 'smooth'
      });
    }

    updateNavigation() {
      if (
        !this.grid ||
        !this.previousButton ||
        !this.nextButton
      ) {
        return;
      }

      const tolerance = 4;

      const maximumScroll =
        this.grid.scrollWidth -
        this.grid.clientWidth;

      const canScroll =
        maximumScroll > tolerance;

      const isAtBeginning =
        this.grid.scrollLeft <= tolerance;

      const isAtEnd =
        this.grid.scrollLeft >=
        maximumScroll - tolerance;

      this.previousButton.disabled =
        !canScroll || isAtBeginning;

      this.nextButton.disabled =
        !canScroll || isAtEnd;
    }

    handleResize() {
      window.clearTimeout(this.resizeTimer);

      this.resizeTimer = window.setTimeout(() => {
        this.updateNavigation();
      }, 100);
    }

    handleVariantChange(selector) {
      const selectedOption =
        selector.options[selector.selectedIndex];

      if (!selectedOption) return;

      const card = selector.closest(SELECTORS.card);

      if (!card) return;

      const button = card.querySelector(SELECTORS.add);

      const buttonText = card.querySelector(
        SELECTORS.buttonText
      );

      const currentPrice = card.querySelector(
        SELECTORS.currentPrice
      );

      const comparePrice = card.querySelector(
        SELECTORS.comparePrice
      );

      const availability = card.querySelector(
        SELECTORS.availability
      );

      const image = card.querySelector(
        SELECTORS.productImage
      );

      const variantId =
        selectedOption.value || '';

      const available =
        selectedOption.dataset.available === 'true';

      const price =
        selectedOption.dataset.price || '';

      const compareAtPrice =
        selectedOption.dataset.comparePrice || '';

      const imageUrl =
        selectedOption.dataset.image || '';

      const imageAlt =
        selectedOption.dataset.imageAlt || '';

      if (button) {
        button.dataset.variantId = variantId;
        button.disabled = !available;

        button.classList.toggle(
          'is-disabled',
          !available
        );
      }

      if (buttonText) {
        buttonText.textContent = available
          ? buttonText.dataset.availableText ||
            'Agregar al carrito'
          : buttonText.dataset.soldOutText ||
            'Agotado';
      }

      if (currentPrice && price) {
        currentPrice.textContent = price;

        currentPrice.classList.toggle(
          'is-sale',
          Boolean(compareAtPrice)
        );
      }

      if (comparePrice) {
        comparePrice.textContent =
          compareAtPrice || '';

        comparePrice.hidden = !compareAtPrice;
      }

      if (availability) {
        availability.textContent = available
          ? availability.dataset.availableText ||
            'Disponible'
          : availability.dataset.soldOutText ||
            'Agotado';

        availability.classList.toggle(
          'is-sold-out',
          !available
        );
      }

      if (image && imageUrl) {
        image.src = imageUrl;

        if (imageAlt) {
          image.alt = imageAlt;
        }
      }
    }

    async handleAddToCart(button) {
      if (
        !button ||
        button.disabled ||
        button.classList.contains('is-loading')
      ) {
        return;
      }

      const variantId =
        Number(button.dataset.variantId);

      if (!variantId) {
        this.showToast(
          'Selecciona una opción disponible.',
          true
        );

        return;
      }

      const quantity =
        Number(button.dataset.quantity) || 1;

      const originalText = button
        .querySelector(SELECTORS.buttonText)
        ?.textContent;

      this.setButtonLoading(button, true);

      try {
        const cartDrawer =
          document.querySelector('cart-drawer');

        const cartNotification =
          document.querySelector(
            'cart-notification'
          );

        const sectionIds = [];

        if (cartDrawer) {
          sectionIds.push(
            'cart-drawer',
            'cart-icon-bubble'
          );
        } else if (cartNotification) {
          sectionIds.push(
            'cart-notification-product',
            'cart-icon-bubble'
          );
        } else {
          sectionIds.push('cart-icon-bubble');
        }

        const root =
          window.Shopify?.routes?.root || '/';

        const response = await fetch(
          `${root}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              items: [
                {
                  id: variantId,
                  quantity
                }
              ],
              sections: sectionIds,
              sections_url:
                window.location.pathname
            })
          }
        );

        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description ||
            result.message ||
            'No pudimos agregar el producto.'
          );
        }

        await this.updateCartInterface(
          result,
          cartDrawer,
          cartNotification
        );

        const successMessage =
          button.dataset.successMessage ||
          'Producto agregado al carrito.';

        this.showToast(successMessage, false);
      } catch (error) {
        console.error(
          '[Chemarry Cat Care]',
          error
        );

        this.showToast(
          error.message ||
            'No pudimos agregar el producto. Inténtalo nuevamente.',
          true
        );
      } finally {
        this.setButtonLoading(
          button,
          false,
          originalText
        );
      }
    }

    async updateCartInterface(
      result,
      cartDrawer,
      cartNotification
    ) {
      if (
        cartDrawer &&
        typeof cartDrawer.renderContents ===
          'function'
      ) {
        cartDrawer.renderContents(result);

        window.setTimeout(() => {
          if (
            typeof cartDrawer.open === 'function'
          ) {
            cartDrawer.open();
          }
        }, 80);

        return;
      }

      if (
        cartNotification &&
        typeof cartNotification.renderContents ===
          'function'
      ) {
        cartNotification.renderContents(result);
        return;
      }

      await this.refreshCartBubble(result.sections);
    }

    async refreshCartBubble(sections = {}) {
      const suppliedBubble =
        sections?.['cart-icon-bubble'];

      if (suppliedBubble) {
        const parser = new DOMParser();

        const documentFragment =
          parser.parseFromString(
            suppliedBubble,
            'text/html'
          );

        const newBubble =
          documentFragment.querySelector(
            '#cart-icon-bubble'
          );

        const currentBubble =
          document.querySelector(
            '#cart-icon-bubble'
          );

        if (newBubble && currentBubble) {
          currentBubble.innerHTML =
            newBubble.innerHTML;

          return;
        }
      }

      try {
        const root =
          window.Shopify?.routes?.root || '/';

        const response = await fetch(
          `${root}cart.js`,
          {
            headers: {
              Accept: 'application/json'
            }
          }
        );

        const cart = await response.json();

        const bubbles =
          document.querySelectorAll(
            '.cart-count-bubble'
          );

        bubbles.forEach((bubble) => {
          bubble.innerHTML =
            `<span aria-hidden="true">${cart.item_count}</span>` +
            `<span class="visually-hidden">${cart.item_count} productos</span>`;
        });
      } catch (error) {
        console.warn(
          '[Chemarry Cat Care] No se pudo actualizar el contador.',
          error
        );
      }
    }

    setButtonLoading(
      button,
      loading,
      originalText = ''
    ) {
      const text = button.querySelector(
        SELECTORS.buttonText
      );

      button.classList.toggle(
        'is-loading',
        loading
      );

      button.setAttribute(
        'aria-busy',
        loading ? 'true' : 'false'
      );

      if (loading) {
        button.disabled = true;

        if (text) {
          text.textContent = 'Agregando…';
        }

        return;
      }

      const variantSelector =
        button
          .closest(SELECTORS.card)
          ?.querySelector(SELECTORS.variant);

      const selectedOption =
        variantSelector?.options[
          variantSelector.selectedIndex
        ];

      const available = selectedOption
        ? selectedOption.dataset.available ===
          'true'
        : button.dataset.available !== 'false';

      button.disabled = !available;

      if (text) {
        text.textContent = available
          ? originalText ||
            text.dataset.availableText ||
            'Agregar al carrito'
          : text.dataset.soldOutText ||
            'Agotado';
      }
    }

    showToast(message, isError = false) {
      if (!this.toast || !this.toastText) return;

      window.clearTimeout(this.toastTimer);

      this.toastText.textContent = message;

      this.toast.classList.toggle(
        'is-error',
        isError
      );

      this.toast.classList.add('is-visible');

      this.toast.setAttribute(
        'aria-hidden',
        'false'
      );

      this.toastTimer = window.setTimeout(() => {
        this.toast.classList.remove(
          'is-visible'
        );

        this.toast.setAttribute(
          'aria-hidden',
          'true'
        );
      }, isError ? 5000 : 3200);
    }

    prefersReducedMotion() {
      return window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
    }

    handleSectionLoad(event) {
      if (!event.target.contains(this)) return;

      this.cacheElements();
      this.updateProducts();
      this.updateNavigation();
    }
  }

  if (
    !customElements.get('chemarry-cat-care')
  ) {
    customElements.define(
      'chemarry-cat-care',
      ChemarryCatCare
    );
  }

  const initializeSections = (container = document) => {
    container
      .querySelectorAll(SELECTORS.root)
      .forEach((section) => {
        if (
          section.dataset.initialized !== 'true' &&
          typeof section.connectedCallback ===
            'function'
        ) {
          section.connectedCallback();
        }
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initializeSections()
    );
  } else {
    initializeSections();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeSections(event.target);
    }
  );
})();